import type { ClinicSession } from "../../shared/api/clinicSessions";

/**
 * #19953 — các buổi khám đang bật không được chồng giờ nhau. Cùng luật với
 * `ClinicSessionService.firstOverlap` ở server (server vẫn kiểm lại khi lưu): trùng khi
 * `a.start < b.end && b.start < a.end`, chạm mép thì hợp lệ. Giờ dạng "HH:mm" so được bằng chuỗi.
 */

const HHMM = /^\d{2}:\d{2}$/;

/** Các buổi đang bật, trừ buổi đang sửa, theo giờ bắt đầu. */
function others(sessions: readonly ClinicSession[], excludeId: string | null): ClinicSession[] {
  return sessions
    .filter((s) => s.active && s.id !== excludeId)
    .slice()
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
}

/** Buổi đầu tiên trùng với [start, end); null khi không trùng hoặc giờ chưa đủ. */
export function overlappingSession(
  sessions: readonly ClinicSession[],
  excludeId: string | null,
  start: string,
  end: string
): ClinicSession | null {
  if (!HHMM.test(start) || !HHMM.test(end) || start >= end) return null;
  return others(sessions, excludeId).find((o) => start < o.endTime && o.startTime < end) ?? null;
}

/** Lời báo trùng giờ cho form; null khi hợp lệ. */
export function sessionOverlapMessage(
  sessions: readonly ClinicSession[],
  excludeId: string | null,
  start: string,
  end: string
): string | null {
  const o = overlappingSession(sessions, excludeId, start, end);
  return o
    ? `Trùng giờ với buổi ${o.label} (${o.startTime}–${o.endTime}). Các buổi khám không được chồng giờ nhau.`
    : null;
}

/** Các khoảng giờ còn trống trong ngày ("HH:mm"–"HH:mm", 24:00 là hết ngày). */
export function freeGaps(
  sessions: readonly ClinicSession[],
  excludeId: string | null
): { start: string; end: string }[] {
  const gaps: { start: string; end: string }[] = [];
  let cursor = "00:00";
  for (const o of others(sessions, excludeId)) {
    if (o.startTime > cursor) gaps.push({ start: cursor, end: o.startTime });
    if (o.endTime > cursor) cursor = o.endTime;
  }
  if (cursor < "24:00") gaps.push({ start: cursor, end: "24:00" });
  return gaps;
}

/**
 * Giờ kết thúc muộn nhất cho một buổi bắt đầu lúc `start` = giờ bắt đầu của buổi kế tiếp; null khi
 * không có buổi nào sau (tới hết ngày) hoặc `start` đang nằm trong một buổi.
 */
export function latestEndFor(
  sessions: readonly ClinicSession[],
  excludeId: string | null,
  start: string
): string | null {
  if (!HHMM.test(start)) return null;
  const list = others(sessions, excludeId);
  if (list.some((o) => o.startTime <= start && start < o.endTime)) return null;
  return list.find((o) => o.startTime > start)?.startTime ?? null;
}
